import { Button, ButtonStyle } from '@davidlloydleisure/common-js-frontend';
import type { PropsWithChildren } from 'react';
import React, { use } from 'react';

import { ErrorBoundary } from 'src/components/common/error-boundary';
import { SiteContext } from 'src/context/site-context';
import { getBrandForBuildType } from 'src/helpers/brand-for-build-type';
import {
  TranslatedStaticText,
  useStaticTextTranslator,
} from 'src/translators/static-text-translator';

import * as style from './page-error-boundary.module.scss';

const handleRetry = (): void => {
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

const LocalisedErrorFallback: React.FC = () => {
  const { buildType } = use(SiteContext);
  const translate = useStaticTextTranslator();

  return (
    <div className={style.errorContainer}>
      <h1 className={style.errorTitle}>
        <TranslatedStaticText lookupKey="somethingHasGoneWrong" />
      </h1>
      <p>
        <TranslatedStaticText lookupKey="pleaseRefreshOrTryAgainLater" />
      </p>
      <Button
        brand={getBrandForBuildType(buildType)}
        style={ButtonStyle.PRIMARY}
        text={translate('retry')}
        onClick={handleRetry}
      />
    </div>
  );
};

const StaticErrorFallback: React.FC = () => (
  <div className={style.errorContainer}>
    <h1 className={style.errorTitle}>Something has gone wrong</h1>
    <p>Please refresh the page or try again later.</p>
    <button type="button" onClick={handleRetry}>
      Retry
    </button>
  </div>
);

export const StaticErrorBoundary: React.FC<PropsWithChildren> = ({ children }) => (
  <ErrorBoundary fallback={<StaticErrorFallback />}>{children}</ErrorBoundary>
);

export const LocalisedErrorBoundary: React.FC<PropsWithChildren> = ({ children }) => (
  <ErrorBoundary fallback={<LocalisedErrorFallback />}>{children}</ErrorBoundary>
);
